import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { getLastResults, getDetailResults } from '@/services/casinoApi';
import "./Result.css";
import ResultModalLayout from '@/components/ResultModalLayout';

// Helper function to map card string to image path
const getCardImage = (cardString) => {
  if (!cardString) return null;
  return `/assets/img/cards/${cardString}.png`;
};

// Helper function to parse card string for baccarat29 (format: "3HH,6HH,3DD,2CC,7SS,7DD" = 3 cards for Player A, 3 cards for Player B)
const parseCards = (cardString) => {
  if (!cardString) return { playerA: [], playerB: [] };
  
  const cards = cardString.split(',');
  const playerACards = cards.slice(0, 3).map(card => getCardImage(card.trim()));
  const playerBCards = cards.slice(3, 6).map(card => getCardImage(card.trim()));
  
  return { playerA: playerACards, playerB: playerBCards };
};

// Helper function to parse rdesc for baccarat29
// Format: "Player B  (A: 3 | B: 5)#Player B#Player A#-#No"
const parseRdesc = (rdesc) => {
  if (!rdesc) return {};
  
  const parts = rdesc.split('#').map(p => p.trim());
  
  return {
    winner: parts[0] || "",
    highCard: parts[1] || "",
    pair: parts[2] || "",
    colorPlus: parts[3] || "",
    lucky9: parts[4] || "",
  };
};

const ResultBaccarat29 = ({ 
  data = [], 
  gameData = {},
}) => {
  const [showModal, setShowModal] = useState(false);
  const [selectedResult, setSelectedResult] = useState(null);
  const [lastResults, setLastResults] = useState([]);
  const [detailResult, setDetailResult] = useState(null);
  const [loading, setLoading] = useState(false);

  // Fetch last results on mount
  useEffect(() => {
    const fetchLastResults = async () => {
      try {
        const response = await getLastResults('teensin');
        if (response?.data?.data?.res) {
          // Map win values: "1" = "A" (Player A), "2" = "B" (Player B)
          const mappedResults = response.data.data.res.map(item => ({
            mid: item.mid,
            win: item.win === "1" ? "A" : item.win === "2" ? "B" : item.win
          }));
          setLastResults(mappedResults);
        }
      } catch (error) {
        console.error('Error fetching last results:', error);
      }
    };
    
    fetchLastResults();
  }, []);

  const handleResultClick = useCallback(async (result, index) => {
    setSelectedResult(result);
    setLoading(true);
    
    // First, ensure we have last results
    if (lastResults.length === 0) {
      try {
        const response = await getLastResults('teensin');
        if (response?.data?.data?.res) {
          const mappedResults = response.data.data.res.map(item => ({
            mid: item.mid,
            win: item.win === "1" ? "A" : item.win === "2" ? "B" : item.win
          }));
          setLastResults(mappedResults);
        }
      } catch (error) {
        console.error('Error fetching last results:', error);
      }
    }
    
    // Find the mid for this result
    let mid = null;
    if (index < lastResults.length) {
      mid = lastResults[index]?.mid;
    } else if (index < data.length + (gameData?.lrs?.length || 0)) {
      mid = gameData?.data?.data?.mid || gameData?.mid;
    }
    
    // Fetch detail results if we have a mid and it's different from current
    if (mid && detailResult?.rid !== mid.toString()) {
      try {
        const response = await getDetailResults('teensin', mid.toString());
        if (response?.data?.data?.t1) {
          setDetailResult(response.data.data.t1);
        }
      } catch (error) {
        console.error('Error fetching detail results:', error);
      }
    }
    
    setLoading(false);
    setShowModal(true);
  }, [lastResults, data, gameData, detailResult]);

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedResult(null);
    setDetailResult(null);
  };

  // Format modal data based on selected result - memoized to prevent re-renders
  const modalData = useMemo(() => {
    if (!selectedResult || !showModal) return null;

    // Use detailResult if available
    const cards = detailResult?.card ? parseCards(detailResult.card) : { 
      playerA: [], 
      playerB: [] 
    };
    
    const rdescData = detailResult?.rdesc ? parseRdesc(detailResult.rdesc) : {};
    const winner = detailResult?.winnat || (selectedResult === "A" ? "Player A" : selectedResult === "B" ? "Player B" : "");
    
    // Determine winner from API data
    const winValue = detailResult?.win;
    const isPlayerAWinner = winValue === "1" || winner === "Player A" || selectedResult === "A";
    const isPlayerBWinner = winValue === "2" || winner === "Player B" || selectedResult === "B";

    return {
      roundId: detailResult?.rid || gameData?.data?.data?.mid || gameData?.mid || "157250124095658",
      matchTime: detailResult?.mtime || gameData?.data?.data?.mt || new Date().toLocaleString(),
      playerA: {
        cards: cards.playerA,
        isWinner: isPlayerAWinner,
      },
      playerB: {
        cards: cards.playerB,
        isWinner: isPlayerBWinner,
      },
      resultDetails: {
        winner: rdescData.winner || winner,
        highCard: rdescData.highCard || "",
        pair: rdescData.pair || "",
        colorPlus: rdescData.colorPlus || "",
        lucky9: rdescData.lucky9 || "",
      },
    };
  }, [selectedResult, detailResult, gameData, showModal]);

  // Combine API results with fallback data
  const allResults = lastResults.length > 0 
    ? lastResults.map(r => r.win)
    : [...data, ...(gameData?.lrs || [])];

  return (
    <>
      <div className="result-row">
        {allResults.map((res, index) => (
          <div
            key={index}
            className={`result-circle ${res === "A" ? "yes" : "no"}`}
            onClick={() => handleResultClick(res, index)}
            style={{ cursor: 'pointer' }}
          >
            {res}
          </div>
        ))}
      </div>

      {/* Modal */}
      {modalData && (
        <ResultModalLayout
          show={showModal}
          onHide={handleCloseModal}
          title="29 CARD BACCARAT"
          roundId={modalData.roundId}
          matchTime={modalData.matchTime}
          loading={loading}
        >
          <div>
            {/* Round ID */}
            <h6 className="text-right round-id" style={{ marginBottom: '1rem' }}>
              <b>Round Id:</b> {modalData.roundId}
            </h6>

            {/* Player Cards Section */}
            <div className="player-image-container row">
              {/* Player A */}
              <div style={{ flex: '1' }} className="text-center player-number">
                <h4>Player A</h4>
                <div style={{ display: 'flex', justifyContent: 'center', gap: '4px', flexWrap: 'wrap' }}>
                  {modalData.playerA?.cards?.map((card, index) => (
                    card && (
                      <img
                        key={index}
                        src={card}
                        alt={`Player A Card ${index + 1}`}
                        onError={(e) => {
                          const cardName = card.split('/').pop();
                          const fallbackPath = `/assets/img/card-results/${cardName}`;
                          if (e.target.src !== fallbackPath) {
                            e.target.src = fallbackPath;
                          } else {
                            e.target.style.display = 'none';
                          }
                        }}
                      />
                    )
                  ))}
                </div>
                <div className="col-md-12 text-center">
                  {modalData.playerA?.isWinner ? (
                    <label className="winner-label bg-success m-t-20">
                      <i className="fas fa-trophy m-r-5"></i>Winner
                    </label>
                  ) : (
                    <label className="m-t-20">&nbsp;</label>
                  )}
                </div>
              </div>

              {/* Player B */}
              <div style={{ flex: '1' }} className="text-center player-number">
                <h4>Player B</h4>
                <div style={{ display: 'flex', justifyContent: 'center', gap: '4px', flexWrap: 'wrap' }}>
                  {modalData.playerB?.cards?.map((card, index) => (
                    card && (
                      <img
                        key={index}
                        src={card}
                        alt={`Player B Card ${index + 1}`}
                        onError={(e) => {
                          const cardName = card.split('/').pop();
                          const fallbackPath = `/assets/img/card-results/${cardName}`;
                          if (e.target.src !== fallbackPath) {
                            e.target.src = fallbackPath;
                          } else {
                            e.target.style.display = 'none';
                          }
                        }}
                      />
                    )
                  ))}
                </div>
                <div className="col-md-12 text-center">
                  {modalData.playerB?.isWinner ? (
                    <label className="winner-label bg-success m-t-20">
                      <i className="fas fa-trophy m-r-5"></i>Winner
                    </label>
                  ) : (
                    <label className="m-t-20">&nbsp;</label>
                  )}
                </div>
              </div>
            </div>
          </div>
        </ResultModalLayout>
      )}
    </>
  );
};

export default ResultBaccarat29;

